/**
 * AssignOneOnOneCoachModal — E18-2
 * Assign or change the coach for a participant's 1:1 sessions.
 * Only shows coaches with confirmed 1:1 availability for this cohort.
 */

import { useState, useEffect } from 'react';
import { Modal, Select, Button, Group, Text, Avatar, Stack, Alert } from '@mantine/core';
import type { ComboboxItem } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { EligibleOneOnOneCoach, OneOnOneParticipant } from '../../../../types';
import sessionsApi from '../../../../services/api/sessions';
import { getApiErrorMessage } from '../../../../services/api/client';
import { AlertCircle } from 'react-feather';
import { NeedsMedleyEmailHint } from '../../../../components/coaches/NeedsMedleyEmailHint';

// MED-1355: the fade for a coach who cannot be picked (Add Potential Coaches uses 0.5).
const BLOCKED_OPACITY = 0.5;

interface AssignOneOnOneCoachModalProps {
  cohortId: string;
  participant: OneOnOneParticipant | null;
  opened: boolean;
  onClose: () => void;
  onAssigned: () => void;
}

export function AssignOneOnOneCoachModal({
  cohortId,
  participant,
  opened,
  onClose,
  onAssigned,
}: AssignOneOnOneCoachModalProps) {
  const [coaches, setCoaches] = useState<EligibleOneOnOneCoach[]>([]);
  const [selectedCoachId, setSelectedCoachId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // MED-1355: the server reason for a refused save. It stays in the modal
  // until the selection changes, so the CM can read it and pick again.
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    if (!opened) return;
    setSelectedCoachId(participant?.coach_id ?? null);
    setServerError(null);
    setLoading(true);
    sessionsApi
      .listEligibleOneOnOneCoaches(cohortId)
      .then(setCoaches)
      .catch(() => notifications.show({ message: 'Failed to load coaches', color: 'red' }))
      .finally(() => setLoading(false));
  }, [opened, cohortId, participant]);

  const handleAssign = async () => {
    if (!participant || !selectedCoachId) return;
    setSaving(true);
    setServerError(null);
    try {
      await sessionsApi.assignOneOnOneCoach(cohortId, participant.user_id, selectedCoachId);
      notifications.show({ message: 'Coach assigned successfully', color: 'green' });
      onAssigned();
      onClose();
    } catch (error) {
      // MED-1355: show the server reason in the modal, for example a coach
      // with no Medley email, and keep the modal open.
      setServerError(getApiErrorMessage(error, 'Failed to assign coach'));
    } finally {
      setSaving(false);
    }
  };

  // MED-1355: a 1:1 coach needs a Medley email, so a coach without one is
  // disabled, with a link to add it.
  const coachOptions: ComboboxItem[] = coaches.map((c) => ({
    value: c.user_id,
    label: `${c.first_name} ${c.last_name}`,
    disabled: c.has_medley_email === false,
  }));

  const handleSelectCoach = (value: string | null) => {
    setSelectedCoachId(value);
    setServerError(null);
  };

  const participantName = participant
    ? `${participant.first_name} ${participant.last_name}`
    : '';

  const selectedCoach = coaches.find((c) => c.user_id === selectedCoachId);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Stack gap={2}>
          <Text fw={700} size="lg">Assign Coach to {participantName}</Text>
          <Text size="sm" c="dimmed">
            Select a coach to assign to this participant. Only coaches with confirmed availability are shown.
          </Text>
        </Stack>
      }
      size="md"
      radius="md"
    >
      <Stack gap="lg" mt="md">
        <Select
          label="Select Coach"
          placeholder={loading ? 'Loading coaches...' : coaches.length === 0 ? 'No eligible coaches' : 'Choose a coach'}
          data={coachOptions}
          value={selectedCoachId}
          onChange={handleSelectCoach}
          disabled={loading || coaches.length === 0}
          searchable
          // A disabled option is faded by Mantine at 0.35 opacity. Keep the
          // option at full opacity and fade only the coach details, so the
          // link keeps its contrast (the same as Add Potential Coaches).
          styles={{ option: { opacity: 1 } }}
          renderOption={({ option }) => {
            const coach = coaches.find((c) => c.user_id === option.value);
            const fade = { opacity: option.disabled ? BLOCKED_OPACITY : 1 };
            return (
              <Group gap="sm" wrap="nowrap">
                <Avatar src={coach?.avatar_url} size={32} radius="xl" color="medleyGreen" style={fade}>
                  {coach?.first_name.charAt(0)}
                </Avatar>
                <div>
                  <Text size="sm" fw={500} style={fade}>{option.label}</Text>
                  {coach?.job_title && (
                    <Text size="xs" c="dimmed" style={fade}>{coach.job_title}</Text>
                  )}
                  {option.disabled && (
                    <NeedsMedleyEmailHint
                      coachProfileId={coach?.coach_profile_id}
                      coachName={option.label}
                      onNavigate={onClose}
                    />
                  )}
                </div>
              </Group>
            );
          }}
        />

        {selectedCoach && (
          <Group gap="sm" p="sm" style={{ backgroundColor: '#F5F3EF', borderRadius: 8 }}>
            <Avatar src={selectedCoach.avatar_url} size={40} radius="xl" color="medleyGreen">
              {selectedCoach.first_name.charAt(0)}
            </Avatar>
            <div>
              <Text size="sm" fw={600}>{selectedCoach.first_name} {selectedCoach.last_name}</Text>
              {selectedCoach.job_title && (
                <Text size="xs" c="dimmed">{selectedCoach.job_title}</Text>
              )}
            </div>
          </Group>
        )}

        {/* MED-1355: the server reason for a refused save, above the buttons. */}
        {serverError && (
          <Alert icon={<AlertCircle size={16} />} color="red" variant="light" role="alert">
            <Text size="sm">{serverError}</Text>
          </Alert>
        )}

        <Group justify="flex-end" gap="sm">
          <Button variant="default" radius="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            radius="sm"
            color="medleyGreen"
            onClick={handleAssign}
            loading={saving}
            disabled={!selectedCoachId || selectedCoachId === participant?.coach_id}
          >
            Assign
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default AssignOneOnOneCoachModal;
